import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
  useSortBy,
} from 'react-table';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import InstallmentDetails from './InstallmentDetails';
import '../../pages/Fees/Fees.css';
import configData from '../../configure';
import { useAuth } from '../../AuthContext';
import jsPDF from 'jspdf';

function FeeDetails({ selectedUser }) {
  const [feeStructure, setFeeStructure] = useState([]);
  const [feePayments, setFeePayments] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [showInstallmentDetails, setShowInstallmentDetails] = useState(false);

  const { user: authUser, studentInfo: authStudentInfo, axios: authAxios } = useAuth();
  const userId = selectedUser?.studentInfo?.userId || selectedUser?.userId || selectedUser?.user?.userId || selectedUser?.id || authStudentInfo?.userId || authUser?.userId || authUser?.id;
  const classId = selectedUser?.studentInfo?.classId || selectedUser?.classId || selectedUser?.user?.classId || authStudentInfo?.classId || authUser?.classId;
  const [feeStructureMeta, setFeeStructureMeta] = useState({});
  console.log("Resolved userId for fee details:", userId, "classId:", classId);
  const [studentFeeProfile, setStudentFeeProfile] = useState(null);

  /* --------------------------  FETCH  -------------------------- */
  const fetchFeeStructure = useCallback(async () => {
    if (!classId) return;
    const TARGET_YEAR = '2025-2026';
    try {
      const { data } = await authAxios.get(
        `${configData.GET_FEE_STRUCTURE}/class/${classId}?academicYear=${TARGET_YEAR}`
      );
      const feeData = data.find(i => i.academicYear === TARGET_YEAR);
      if (!feeData?.feeInstallments) {
        setFeeStructure([]);
        setFeeStructureMeta({});
        return;
      }
      const terms = feeData.feeInstallments.filter(i => !i.isAnnual);
      setFeeStructure(terms);
      setFeeStructureMeta({
        feeStructureId: feeData.feeStructureId,
        orgId: feeData.orgId,
        academicYear: feeData.academicYear,
      });
    } catch (e) {
      console.error(e);
      setFeeStructure([]);
      setFeeStructureMeta({});
    }
  }, [authAxios, classId]);

  const fetchStudentFeeProfile = useCallback(async () => {
    if (!userId) return;
    const TARGET_YEAR = '2025-2026';
    const orgId = feeStructureMeta.orgId || authUser?.orgId || '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
    try {
      const { data } = await authAxios.get(
        `${configData.STUDENT_FEE_PROFILE}/student/${userId}?orgId=${orgId}&academicYear=${TARGET_YEAR}`
      );
      setStudentFeeProfile(data || null);
    } catch (e) {
      setStudentFeeProfile(null);
    }
  }, [authAxios, userId, feeStructureMeta.orgId, authUser?.orgId]);

  const fetchFeePayments = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await authAxios.get(`${configData.INFRASTRUCTURE_FEEPAYMENT}/student/${userId}`);
      const paymentsList = Array.isArray(data) ? data : (data?.items || data?.content || []);
      setFeePayments(paymentsList);
    } catch (e) {
      console.error(e);
    }
  }, [authAxios, userId]);

  useEffect(() => { fetchFeeStructure(); }, [fetchFeeStructure]);
  useEffect(() => { fetchFeePayments(); }, [fetchFeePayments]);
  useEffect(() => { fetchStudentFeeProfile(); }, [fetchStudentFeeProfile]);

  const refreshData = () => {
    fetchFeeStructure();
    fetchFeePayments();
    fetchStudentFeeProfile();
  };

  /* --------------------------  HELPERS  -------------------------- */
  const generateAnnualFeeRow = (terms) => {
    if (!terms.length) return null;
    const cats = [
      'Admission Fee', 'Tuition Fee', 'SmartClass Fee', 'Computer Fee',
      'Sports Fee', 'Library Fee', 'Extracurricular Fee', 'Examination Fee',
    ];
    const annual = {};
    cats.forEach(c => {
      annual[c] = terms.reduce((s, t) => s + (Number(t.feeBreakdown?.[c]) || 0), 0);
    });
    const totalCats = Object.values(annual).reduce((s, v) => s + Number(v || 0), 0);
    const totalTerms = terms.reduce((s, t) => s + Number(t.totalFee || t.totalAmount || t.amount || 0), 0);
    const total = totalCats > 0 ? totalCats : totalTerms;

    const start = terms.map(t => new Date(t.startDate)).filter(d => !isNaN(d));
    const end = terms.map(t => new Date(t.endDate)).filter(d => !isNaN(d));
    const due = terms.map(t => new Date(t.dueDate)).filter(d => !isNaN(d));

    return {
      description: 'Total Annual Fee',
      startDate: start.length ? new Date(Math.min(...start)).toISOString() : '',
      endDate: end.length ? new Date(Math.max(...end)).toISOString() : '',
      dueDate: due.length
        ? new Date(new Date(Math.min(...due)).getTime() + 15 * 24 * 60 * 60 * 1000).toISOString()
        : '',
      feeBreakdown: annual,
      totalFee: total,
      isAnnual: true,
      installmentId: 'ANNUAL_TOTAL', // ← Important for payment
    };
  };

  const isStatusPaid = (statusStr) => {
    if (!statusStr) return false;
    const s = String(statusStr).toUpperCase();
    return s === 'SUCCESS' || s === 'COMPLETED' || s === 'PAID';
  };

  const isStatusPending = (statusStr) => {
    if (!statusStr) return false;
    const s = String(statusStr).toUpperCase();
    return s === 'PENDING_VERIFICATION' || s === 'PENDING' || s === 'SUBMITTED';
  };

  const getPendingPayment = (installmentId) => {
    const payments = Array.isArray(feePayments) ? feePayments : (feePayments?.items || feePayments?.content || []);
    return payments.find(
      p =>
        (p.installmentId === installmentId || p.installmentId === 'ANNUAL_TOTAL' || installmentId === 'ANNUAL_TOTAL') &&
        (!p.feeStructureId || !feeStructureMeta.feeStructureId || p.feeStructureId === feeStructureMeta.feeStructureId) &&
        (isStatusPending(p.paymentStatus) || isStatusPending(p.status))
    );
  };

  const userRole = (authUser?.userType || authUser?.role || authUser?.userRole || '').toUpperCase();
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPERADMIN' || userRole === 'STAFF' || userRole === 'FACULTY' || userRole === 'ROLE_ADMIN';

  const handleApprovePayment = async (payment) => {
    try {
      const now = new Date().toISOString();
      const payload = {
        ...payment,
        paymentStatus: "SUCCESS",
        status: "SUCCESS",
        markAsSuccess: true,
        lastUpdatedBy: authUser?.username || authUser?.email || "admin",
        lastUpdatedAt: now,
      };
      await authAxios.post(configData.INFRASTRUCTURE_FEEPAYMENT, payload);
      alert("Payment verified & approved successfully!");
      refreshData();
    } catch (err) {
      console.error("Failed to approve payment:", err);
      alert("Failed to approve payment");
    }
  };

  const handleRejectPayment = async (payment) => {
    const reason = window.prompt("Reason for rejection (Optional):", "UTR not found in bank statement");
    if (reason === null) return;
    try {
      const now = new Date().toISOString();
      const payload = {
        ...payment,
        paymentStatus: "REJECTED",
        status: "REJECTED",
        markAsSuccess: false,
        remarks: `Rejected by Admin: ${reason}`,
        lastUpdatedBy: authUser?.username || authUser?.email || "admin",
        lastUpdatedAt: now,
      };
      await authAxios.post(configData.INFRASTRUCTURE_FEEPAYMENT, payload);
      alert("Payment submission rejected");
      refreshData();
    } catch (err) {
      console.error("Failed to reject payment:", err);
      alert("Failed to reject payment");
    }
  };

  const isAllPaid = useMemo(() => {
    const annualTotal = feeStructure.reduce((s, t) => s + (t.totalFee || 0), 0);
    const payments = Array.isArray(feePayments) ? feePayments : (feePayments?.items || feePayments?.content || []);
    if (annualTotal === 0 && payments.length === 0) return false;

    // Check if annual total payment exists
    const hasAnnualPayment = payments.some(p => (p.installmentId === 'ANNUAL_TOTAL') && (isStatusPaid(p.paymentStatus) || isStatusPaid(p.status)));
    if (hasAnnualPayment) return true;

    const paidTotal = payments
      .filter(p =>
        (!p.feeStructureId || !feeStructureMeta.feeStructureId || p.feeStructureId === feeStructureMeta.feeStructureId) &&
        (isStatusPaid(p.paymentStatus) || isStatusPaid(p.status))
      )
      .reduce((s, p) => s + (p.amountPaid || p.amount || 0), 0);
    return annualTotal > 0 && paidTotal >= annualTotal;
  }, [feeStructure, feePayments, feeStructureMeta.feeStructureId]);

  const isInstallmentPaid = (installmentId) => {
    if (isAllPaid) return true;
    const payments = Array.isArray(feePayments) ? feePayments : (feePayments?.items || feePayments?.content || []);
    if (installmentId === 'ANNUAL_TOTAL') {
      return payments.some(
        p =>
          p.installmentId === 'ANNUAL_TOTAL' &&
          (!p.feeStructureId || !feeStructureMeta.feeStructureId || p.feeStructureId === feeStructureMeta.feeStructureId) &&
          (isStatusPaid(p.paymentStatus) || isStatusPaid(p.status))
      );
    }
    return payments.some(
      p =>
        (p.installmentId === installmentId || p.installmentId === 'ANNUAL_TOTAL') &&
        (!p.feeStructureId || !feeStructureMeta.feeStructureId || p.feeStructureId === feeStructureMeta.feeStructureId) &&
        (isStatusPaid(p.paymentStatus) || isStatusPaid(p.status))
    );
  };

  /* --------------------------  TABLE DATA  -------------------------- */
  const data = useMemo(() => {
    const profileOverrides = studentFeeProfile?.studentFeeOverrides || [];
    const overrideMap = {};
    profileOverrides.forEach(ov => {
      if (ov.baseInstallmentId) {
        overrideMap[ov.baseInstallmentId] = ov;
      }
    });

    const terms = feeStructure.map(t => {
      const instId = t.installmentId || t.id;
      const ov = overrideMap[instId];
      const breakdownSum = Object.values(t.feeBreakdown || {}).reduce((a, b) => a + (Number(b) || 0), 0);
      const baseTotal = Number(t.totalFee || t.totalAmount || t.amount || breakdownSum) || 0;

      let netPayable = baseTotal;
      let discounts = [];
      let customBreakdown = t.feeBreakdown || {};

      if (ov) {
        if (ov.customFeeBreakdown && Object.keys(ov.customFeeBreakdown).length > 0) {
          customBreakdown = ov.customFeeBreakdown;
        }
        discounts = ov.discounts || [];
        if (ov.netPayableFee !== undefined && ov.netPayableFee !== null) {
          netPayable = Number(ov.netPayableFee);
        } else if (discounts.length > 0) {
          const discAmt = discounts.reduce((s, d) => s + Number(d.amount || 0), 0);
          netPayable = Math.max(0, baseTotal - discAmt);
        }
      }

      return {
        ...t,
        baseTotal,
        feeBreakdown: customBreakdown,
        totalFee: netPayable,
        discounts,
        hasOverride: !!ov && discounts.length > 0,
      };
    });

    const annual = generateAnnualFeeRow(terms);
    if (annual && studentFeeProfile?.netTotalFee !== undefined && studentFeeProfile?.netTotalFee !== null) {
      annual.totalFee = studentFeeProfile.netTotalFee;
    }
    return annual ? [...terms, annual] : terms;
  }, [feeStructure, studentFeeProfile]);

  /* --------------------------  COLUMNS  -------------------------- */
  const COLUMNS = useMemo(
    () => [
      { Header: 'Name', accessor: 'description' },
      {
        Header: 'Due Date',
        accessor: 'dueDate',
        Cell: ({ value }) => (value ? new Date(value).toISOString().split('T')[0] : ''),
      },
      { Header: 'Admission Fee', accessor: 'feeBreakdown.Admission Fee' },
      { Header: 'Tuition Fee', accessor: 'feeBreakdown.Tuition Fee' },
      { Header: 'SmartClass Fee', accessor: 'feeBreakdown.SmartClass Fee' },
      { Header: 'Examination Fee', accessor: 'feeBreakdown.Examination Fee' },
      { Header: 'Computer Fee', accessor: 'feeBreakdown.Computer Fee' },
      { Header: 'Sports Fee', accessor: 'feeBreakdown.Sports Fee' },
      { Header: 'Library Fee', accessor: 'feeBreakdown.Library Fee' },
      { Header: 'Extracurricular Fee', accessor: 'feeBreakdown.Extracurricular Fee' },
      {
        Header: 'Total Fee',
        accessor: 'baseTotal',
        Cell: ({ value, row }) => {
          const item = row.original;
          const val = item.baseTotal !== undefined && item.baseTotal !== null
            ? item.baseTotal
            : (item.totalFee || 0);
          return (
            <span style={{ color: '#475569', fontWeight: 600, fontSize: '0.88rem' }}>
              ₹{Number(val || 0).toLocaleString('en-IN')}
            </span>
          );
        },
      },
      {
        Header: 'Applied Concession',
        accessor: 'discounts',
        Cell: ({ row }) => {
          const item = row.original;
          const hasDiscount = item.hasOverride && item.discounts && item.discounts.length > 0;
          const disc = hasDiscount ? item.discounts[0] : null;

          if (item.isAnnual) {
            let totalAnnualDisc = 0;
            if (studentFeeProfile && studentFeeProfile.studentFeeOverrides) {
              studentFeeProfile.studentFeeOverrides.forEach(ov => {
                if (ov.discounts) {
                  ov.discounts.forEach(d => {
                    totalAnnualDisc += Number(d.amount || 0);
                  });
                }
              });
            }
            if (totalAnnualDisc > 0) {
              return (
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    backgroundColor: '#ecfdf5',
                    color: '#047857',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    border: '1px solid #a7f3d0'
                  }}
                >
                  Total Concession (-₹{totalAnnualDisc.toLocaleString('en-IN')})
                </span>
              );
            }
            return <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>;
          }

          if (hasDiscount && disc) {
            return (
              <span
                style={{
                  display: 'inline-block',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  backgroundColor: '#ecfdf5',
                  color: '#047857',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  border: '1px solid #a7f3d0'
                }}
                title={disc.remarks ? `Reason: ${disc.remarks}` : disc.title}
              >
                {disc.title} (-₹{Number(disc.amount || 0).toLocaleString('en-IN')})
              </span>
            );
          }

          return <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>;
        },
      },
      {
        Header: 'Final Fee',
        accessor: 'totalFee',
        Cell: ({ value, row }) => {
          const item = row.original;
          const hasDiscount = item.hasOverride && item.discounts && item.discounts.length > 0;

          return (
            <span
              style={{
                fontWeight: '700',
                color: hasDiscount ? '#047857' : '#014486',
                fontSize: '0.92rem'
              }}
            >
              ₹{Number(value || 0).toLocaleString('en-IN')}
            </span>
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }) => {
          const inst = row.original;
          const isPaid = isAllPaid || isInstallmentPaid(inst.installmentId);
          const pendingPayment = getPendingPayment(inst.installmentId);

          if (isPaid) {
            return (
              <button
                disabled
                style={{
                  backgroundColor: '#28a745',
                  color: '#fff',
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'default',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                }}
              >
                Paid
              </button>
            );
          }

          if (pendingPayment) {
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <span
                  style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.78rem',
                    fontWeight: 'bold',
                    border: '1px solid #ffeeba',
                    textAlign: 'center',
                  }}
                  title={`UTR: ${pendingPayment.transactionId}`}
                >
                  Pending Admin Approval
                  <br />
                  <small style={{ fontSize: '0.7rem' }}>UTR: {pendingPayment.transactionId}</small>
                </span>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                    <button
                      onClick={() => handleApprovePayment(pendingPayment)}
                      style={{
                        backgroundColor: '#28a745',
                        color: '#fff',
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleRejectPayment(pendingPayment)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: '#fff',
                        padding: '4px 8px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          }

          return (
            <button
              onClick={() => {
                setSelectedInstallment(inst);
                setShowInstallmentDetails(true);
              }}
              style={{
                backgroundColor: '#007bff',
                color: '#fff',
                padding: '6px 12px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {inst.isAnnual ? 'Pay Full Year' : 'Pay Now'}
            </button>
          );
        },
      },
    ],
    [isAllPaid, feePayments, studentFeeProfile]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = useTable(
    { columns: COLUMNS, data, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  /* --------------------------  PAYMENT HISTORY  -------------------------- */
  const PAYMENT_COLUMNS = useMemo(
    () => [
      { Header: 'Receipt No.', accessor: 'receiptNumber', Cell: ({ value }) => value || 'N/A' },
      {
        Header: 'Installment',
        accessor: 'installmentId',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          if (value === 'ANNUAL_TOTAL') return 'Full Year Payment';
          const match = feeStructure.find(t => t.installmentId === value);
          return match?.description || 'Fee Installment';
        }
      },
      { Header: 'Amount Paid', accessor: 'amountPaid', Cell: ({ value }) => `₹${value?.toLocaleString() || 0}` },
      { Header: 'Payment Date', accessor: 'paymentDate', Cell: ({ value }) => (value ? new Date(value).toLocaleDateString() : 'N/A') },
      { Header: 'Status', accessor: 'paymentStatus', Cell: ({ value }) => <span className={`payment-status ${value?.toLowerCase()}`}>{value || 'UNKNOWN'}</span> },
      { Header: 'Payment Mode', accessor: 'paymentMode', Cell: ({ value }) => value || 'N/A' },
      { Header: 'Academic Year', accessor: 'academicYear', Cell: ({ value }) => value || 'N/A' },
      {
        Header: 'Fee Breakdown',
        accessor: 'feeBreakdownPaid',
        Cell: ({ value }) => {
          if (!value || typeof value !== 'object') return 'N/A';
          return (
            <div className="fee-breakdown-cell">
              {Object.entries(value).map(([k, v]) => (
                <div key={k} className="breakdown-item"><small>{k}: ₹{v}</small></div>
              ))}
            </div>
          );
        },
      },
      {
        Header: 'Actions',
        Cell: ({ row }) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadReceipt(row.original)}
            sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
          >
            Receipt
          </Button>
        ),
      },
    ],
    [feeStructure]
  );

  const paymentData = useMemo(() => feePayments, [feePayments]);

  const {
    getTableProps: getPaymentTableProps,
    getTableBodyProps: getPaymentTableBodyProps,
    headerGroups: paymentHeaderGroups,
    page: paymentPage,
    prepareRow: preparePaymentRow,
  } = useTable(
    { columns: PAYMENT_COLUMNS, data: paymentData, initialState: { pageIndex: 0 } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  /* --------------------------  RECEIPT  -------------------------- */
  const downloadReceipt = (payment) => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.setTextColor(41, 128, 185);
    doc.text('EGurukul Payment Receipt', 105, 20, { align: 'center' });
    doc.setDrawColor(41, 128, 185);
    doc.setLineWidth(0.5);
    doc.rect(10, 10, 190, 277);

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Payment Receipt', 20, 40);
    doc.setFontSize(10);
    doc.text(`Date: ${payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, 50);
    doc.text(`Receipt No: ${payment.receiptNumber || 'N/A'}`, 20, 55);
    doc.line(20, 60, 190, 60);

    doc.setFontSize(12);
    doc.text('Student Details', 20, 70);
    doc.setFontSize(10);
    doc.text(`Name: ${selectedUser?.user?.firstName || 'N/A'} ${selectedUser?.user?.lastName || 'N/A'}`, 20, 80);
    doc.text(`Phone: ${selectedUser?.user?.phoneNumber || 'N/A'}`, 20, 85);
    doc.text(`Email: ${selectedUser?.user?.email || 'N/A'}`, 20, 90);
    doc.text(`Address: ${selectedUser?.user?.addressDetails?.[0]?.city || 'N/A'}`, 20, 95);
    doc.line(20, 100, 190, 100);

    doc.setFontSize(12);
    doc.text('Payment Details', 20, 110);
    doc.setFontSize(10);
    const installmentName = (() => {
      if (payment.installmentId === 'ANNUAL_TOTAL') return 'Full Year Payment';
      const match = feeStructure.find(t => t.installmentId === payment.installmentId);
      return match?.description || 'Fee Installment';
    })();
    doc.text(`Installment: ${installmentName}`, 20, 120);
    doc.text(`Amount Paid: ${payment.amountPaid?.toLocaleString() || 'N/A'}`, 20, 125);
    doc.text(`Payment Mode: ${payment.paymentMode || 'N/A'}`, 20, 130);
    doc.text(`Academic Year: ${payment.academicYear || 'N/A'}`, 20, 135);

    let y = 145;
    if (payment.feeBreakdownPaid && typeof payment.feeBreakdownPaid === 'object') {
      doc.text('Fee Breakdown:', 20, y);
      y += 5;
      Object.entries(payment.feeBreakdownPaid).forEach(([k, v]) => {
        doc.text(`${k}: ${v}`, 25, y);
        y += 5;
      });
    }

    doc.setFontSize(12);
    doc.setTextColor(46, 125, 50);
    doc.text(`Payment Status: ${payment.paymentStatus || 'N/A'}`, 20, y + 10);

    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('This is a computer-generated receipt and does not require a signature.', 105, 270, { align: 'center' });
    doc.text('For any queries, please contact support@egurukul.com', 105, 275, { align: 'center' });

    doc.save(`payment_receipt_${payment.receiptNumber || payment.installmentId || 'unknown'}.pdf`);
  };

  /* --------------------------  RENDER  -------------------------- */
  if (showInstallmentDetails) {
    return (
      <InstallmentDetails
        selectedInstallment={selectedInstallment}
        totalFee={selectedInstallment?.totalFee}
        onBack={() => setShowInstallmentDetails(false)}
        selectedUser={selectedUser}
        feeStructureMeta={feeStructureMeta}
        onPaymentComplete={refreshData}
      />
    );
  }

  return (
    <div className="wrapFees">
      {/* Fee Structure Table */}
      <div className="fees">
        <h3>Fee Structure & Concessions</h3>

        {studentFeeProfile && studentFeeProfile.studentFeeOverrides && studentFeeProfile.studentFeeOverrides.some(o => o.discounts && o.discounts.length > 0) && (
          <div style={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '1px solid #6ee7b7',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                background: '#047857',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '18px'
              }}>
                %
              </div>
              <div>
                <h4 style={{ margin: 0, color: '#064e3b', fontSize: '15px', fontWeight: 'bold' }}>
                  Fee Concession Active
                </h4>
                <p style={{ margin: '2px 0 0 0', color: '#047857', fontSize: '13px' }}>
                  Individual student discount applied for Academic Year {studentFeeProfile.academicYear || '2025-2026'}
                </p>
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '11px', color: '#047857', fontWeight: 'bold', textTransform: 'uppercase' }}>Net Annual Fee Payable</span>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#064e3b' }}>
                ₹{studentFeeProfile.netTotalFee?.toLocaleString('en-IN') || 0}
              </div>
            </div>
          </div>
        )}

        {data.length ? (
          <table className="tableFees" {...getTableProps()}>
            <thead>
              {headerGroups.map(hg => (
                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                  {hg.headers.map(col => (
                    <th
                      {...col.getHeaderProps()}
                      style={{ textAlign: col.id === 'Name' ? 'left' : 'center' }}
                      key={col.id}
                    >
                      {col.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                const isAnnual = row.original.isAnnual;
                return (
                  <tr
                    {...row.getRowProps()}
                    style={isAnnual ? { fontWeight: 'bold', backgroundColor: '#f9f9f9' } : {}}
                    key={row.id}
                  >
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        style={{ textAlign: 'left', paddingLeft: '10px' }}
                        key={cell.column.id}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No fee structure available.
          </p>
        )}
      </div>

      {/* Payment History */}
      <div className="fees" style={{ marginTop: '30px' }}>
        <h3>Payment History</h3>
        {feePayments.length ? (
          <table className="tableFees" {...getPaymentTableProps()}>
            <thead>
              {paymentHeaderGroups.map(hg => (
                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                  {hg.headers.map(col => (
                    <th {...col.getHeaderProps()} style={{ textAlign: 'center' }} key={col.id}>
                      {col.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getPaymentTableBodyProps()}>
              {paymentPage.map(row => {
                preparePaymentRow(row);
                return (
                  <tr {...row.getRowProps()} key={row.id}>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()} style={{ textAlign: 'left', paddingLeft: '10px' }} key={cell.column.id}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No payment history found.
          </p>
        )}
      </div>
    </div>
  );
}

export default FeeDetails;